import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { BookOpenIcon, CheckCircle2Icon, LockIcon } from "lucide-react";
import { useLearner } from "../../contexts/LearnerContext";
import { units } from "../../data/courses";
import { bookChapters } from "../../data/book";
import { useRouteCourse } from "../../hooks/useRouteCourse";
import { PageHeader } from "../../components/app/PageHeader";
import { Segmented } from "../../components/ui/Segmented";
import { PairChips } from "../../components/ui/LangChip";
import { ProgressBar } from "../../components/ui/ProgressBar";
import { BrutalButton } from "../../components/ui/BrutalButton";
import { LessonNodeButton } from "../../components/courses/LessonNodeButton";
import { ThemedBackdrop } from "../../components/learn/ThemedBackdrop";
import { courseTitle, nextLevel } from "../../utils/language";
import { lessonStatus } from "../../utils/course";
import { bookPath, lessonPath } from "../../utils/routes";
const offsets = [0, 56, 84, 56, 0, -56, -84, -56];
export function CoursePath() {
  const {
    setActiveCourseId
  } = useLearner();
  const course = useRouteCourse();
  const [tab, setTab] = useState<"path" | "book">("path");
  useEffect(() => {
    if (course) setActiveCourseId(course.id);
  }, [course, setActiveCourseId]);
  if (!course) return <Navigate to="/app/courses" replace data-id="element-1254" data-mp="rftdnpkb5ea9" />;
  return <>
    <ThemedBackdrop target={course.target} data-id="element-1255" data-mp="cs887ozyn2z6" />
    <div className="relative z-10 mx-auto max-w-3xl px-4 py-6 md:px-8 md:py-10" data-id="element-1256" data-mp="cr6x5kiy2ghg">
      <PageHeader eyebrow={<PairChips target={course.target} base={course.base} data-id="element-1258" data-mp="hlb1dw0tsaeu" />} title={courseTitle(course)} description={`Level ${course.level} · ${course.progress}% of the way to ${nextLevel(course.level)} · ${course.wordsLearned} words learned`} data-id="element-1257" data-mp="hkrlslyndy3z" />
      <div className="mt-6" data-id="element-1259" data-mp="3q01ljnf2er8l">
        <Segmented id="course-tab" label="Course view" value={tab} onChange={setTab} options={[{
          value: "path",
          label: "Lesson path"
        }, {
          value: "book",
          label: "Book"
        }]} data-id="element-1260" data-mp="3pzs26ioizibv" />
      </div>

      {tab === "path" ? <div className="mt-8 space-y-12" data-id="element-1261" data-mp="370oopfbmit6d">
          {units.map(u => {
          const isCurrent = u.number === course.unit;
          const isDone = u.number < course.unit;
          return <section key={u.number} aria-labelledby={`unit-${u.number}`} data-id="element-1262" data-mp="2x2qzaoi12qc6">
                <div className={`rounded-card border-3 border-line p-5 text-ink ${isCurrent ? "tex-ink-dots bg-brand-tint shadow-brutal" : isDone ? "bg-surface shadow-brutal-sm" : "border-line/40 bg-sunken"}`} data-id="element-1263" data-mp="3uru4juhkdlgo">
                  <div className="flex items-start justify-between gap-4" data-id="element-1264" data-mp="3fua1wu8542do">
                    <div data-id="element-1265" data-mp="29f9e5bt9qqi6">
                      <p className="text-sm font-semibold text-ink-2" data-id="element-1266" data-mp="29gkjvh69y8bx">Unit {u.number}</p>
                      <h2 id={`unit-${u.number}`} className="text-xl font-bold tracking-[-0.01em] md:text-2xl" data-id="element-1267" data-mp="24f4y1mw9vxsh">
                        {u.title}
                      </h2>
                      <p className="mt-1 text-sm text-ink-2" data-id="element-1268" data-mp="owff081oom53">{u.description}</p>
                    </div>
                    {isDone ? <CheckCircle2Icon className="h-7 w-7 shrink-0 text-good" strokeWidth={2.25} aria-label="Completed" data-id="element-1269" data-mp="tugyuv6xophm" /> : !isCurrent ? <LockIcon className="h-6 w-6 shrink-0 text-ink-2" aria-label="Locked" data-id="element-1270" data-mp="13mecs9pbtvk1" /> : null}
                  </div>
                </div>
                <ol className="mt-8 flex flex-col items-center gap-7" data-id="element-1271" data-mp="17pay486945ro">
                  {u.lessons.map((l, i) => <LessonNodeButton key={l.id} lesson={l} status={lessonStatus(course, u.number, i)} href={lessonPath(course, l.id)} offset={offsets[i % offsets.length]} data-id="element-1272" data-mp="1hldx555t3oae" data-mpi={i} />)}
                </ol>
              </section>;
        })}
        </div> : <div className="mt-8 space-y-3" data-id="element-1273" data-mp="18qf1bzoz6ogj">
          {bookChapters.map(ch => <div key={ch.id} className={`flex items-center gap-4 rounded-card border-3 p-4 ${ch.status === "reading" ? "border-line bg-accent/70 text-night shadow-brutal-sm" : ch.status === "locked" ? "border-dashed border-line/40 bg-transparent" : "border-line bg-surface"}`} data-id="element-1274" data-mp="1mp4zlinj6u6h">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border-2 border-line/60 bg-surface text-lg font-bold text-ink" data-id="element-1275" data-mp="13rkk326np7yj">
                {ch.number}
              </span>
              <div className="min-w-0 flex-1" data-id="element-1276" data-mp="b96m92lbllzg">
                <p className="truncate text-[15px] font-bold" data-id="element-1277" data-mp="b7vj94zp8i9e">{ch.title}</p>
                <p className={`text-sm ${ch.status === "reading" ? "text-night/75" : "text-ink-2"}`} data-id="element-1278" data-mp="78eao2oz7500">
                  {ch.titleTranslation} · {ch.pages} pages
                </p>
                {ch.status === "reading" && <ProgressBar value={(course.bookPage + 1) / ch.pages * 100} tone="brand" size="sm" className="mt-2 max-w-[220px]" label="Chapter progress" data-id="element-1279" data-mp="3jbxttgwgdifv" />}
              </div>
              {ch.status === "reading" ? <BrutalButton to={bookPath(course)} variant="surface" size="sm" data-id="element-1280" data-mp="kts8tu7jxii1">
                  <BookOpenIcon className="h-4 w-4" data-id="element-1281" data-mp="5vzw19te387j" /> Continue
                </BrutalButton> : ch.status === "read" ? <Link to={bookPath(course)} className="focus-ring rounded-lg px-2 py-1 text-sm font-semibold text-ink-2 underline underline-offset-4 hover:text-ink" data-id="element-1282" data-mp="ay7dgewzngh9">
                  Re-read
                </Link> : <LockIcon className="h-5 w-5 text-ink-2" aria-label="Locked" data-id="element-1283" data-mp="123cv3zhpz0q4" />}
            </div>)}
        </div>}
    </div>
    </>;
}